import { breakReduction, calendarDaysBetween, localDay, restartWeight } from './schedule.ts';
import type { EventOf, ExerciseId, RepwiseEvent } from './events.ts';
import { WORKOUTS, type MainLift, type WorkoutKind } from './lifts.ts';
import { convert, mass, type Mass, type Unit } from './mass.ts';
import { unitTimeline } from './programme.ts';
import { classify, decide, type LiftState, type SessionOutcome } from './progression.ts';
import type { Settings } from './settings.ts';

/**
 * History import (ADR-0017, US-27). Imported workouts become history-only `workout_imported` events;
 * progression only moves when the user accepts `continueFromImport`'s suggestion.
 */

export interface ImportedExercise {
  /** null = an exercise Repwise doesn't track; kept under its original name. */
  exercise: ExerciseId | null;
  name: string;
  /** In the programme's unit; null for bodyweight exercises. */
  weight: Mass | null;
  /** Reps per programmed set, in order; null = a programmed set with nothing recorded. */
  reps: (number | null)[];
}

export interface ImportedWorkout {
  /** Idempotency key: `<source>:<yyyy-mm-dd>:<name>`. */
  key: string;
  performedAt: number;
  name: string;
  kind: WorkoutKind | null;
  unit: Unit;
  bodyweight?: Mass;
  note?: string;
  exercises: ImportedExercise[];
}

export type ImportFormat = 'stronglifts-legacy' | 'stronglifts-2025' | 'strong' | 'hevy';
export type ImportSource = 'stronglifts' | 'strong' | 'hevy';

export interface ParsedImport {
  format: ImportFormat;
  workouts: ImportedWorkout[];
  /** Exercise names that didn't map to a Repwise exercise (kept, not dropped). */
  unmapped: string[];
  dateOrder: 'mdy' | 'dmy';
  /** True when no date in the file could tell month/day from day/month. */
  dateOrderAssumed: boolean;
  skipped: { line: number; reason: string }[];
}

export class ImportError extends Error {
  constructor(
    readonly code: 'empty' | 'unrecognised' | 'mixed_dates',
    message: string,
  ) {
    super(message);
  }
}

/** RFC 4180-ish: quoted fields, doubled quotes, CRLF, a BOM; `,` or `;` chosen from the header line. */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, '');
  const firstLine = src.slice(0, src.search(/\r?\n|$/));
  const delim = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') (field += '"'), i++;
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"' && field === '') quoted = true;
    else if (ch === delim) row.push(field), (field = '');
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(field), rows.push(row), (row = []), (field = '');
    } else field += ch;
  }
  if (field !== '' || row.length) row.push(field), rows.push(row);
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

const NAMES: [RegExp, ExerciseId][] = [
  [/^(back )?squats?$/, 'squat'],
  [/^(barbell |flat )?bench( press)?$/, 'bench'],
  [/^((barbell|pendlay|bent[- ]over) )?rows?$/, 'row'],
  [/^(overhead|military|standing)( barbell)? press$|^ohp$|^press$/, 'ohp'],
  [/^(conventional )?deadlifts?$/, 'deadlift'],
  [/^(dumbbell |db |barbell )?(bicep )?curls?$/, 'curl'],
  [/^push[- ]?ups?$/, 'pushup'],
  [/^bent over row$/, 'row'],
];

export function mapExercise(name: string): ExerciseId | null {
  // Strong and Hevy put the equipment in brackets: "Squat (Barbell)". 5×5 lifts must be barbell lifts, so
  // "Squat (Smith Machine)" stays unmapped rather than driving barbell targets; curls must be dumbbell.
  const m = /^(.*?)\s*\(([^)]*)\)\s*$/.exec(name.trim());
  const base = (m ? m[1]! : name).trim().toLowerCase().replace(/\s+/g, ' ');
  const equip = m ? m[2]!.trim().toLowerCase() : null;
  const id = NAMES.find(([re]) => re.test(base))?.[1] ?? null;
  if (!id || equip === null) return id;
  if (id === 'curl') return equip === 'dumbbell' ? id : null;
  if (id === 'pushup') return id;
  return equip === 'barbell' ? id : null;
}

/** A/B from what was lifted; null when neither pattern is clear. */
function inferKind(ex: ImportedExercise[]): WorkoutKind | null {
  const has = (l: ExerciseId) => ex.some((e) => e.exercise === l);
  const a = has('bench') || has('row');
  const b = has('ohp') || has('deadlift');
  return a === b ? null : a ? 'A' : 'B';
}

const num = (s: string | undefined): number | null => {
  const t = (s ?? '').trim().replace(',', '.');
  if (t === '') return null;
  const v = Number(t);
  return Number.isFinite(v) ? v : null;
};

/** Parse a StrongLifts export. Only layouts we've verified are accepted; anything else is refused, not guessed. */
export function parseStrongLifts(text: string, opts: { dateOrder?: 'mdy' | 'dmy' } = {}): ParsedImport {
  const rows = parseCsv(text);
  if (rows.length < 2) throw new ImportError('empty', 'This file has no workouts in it.');
  const header = rows[0]!.map((h) => h.trim());
  const blocks = header.flatMap((h, i) => (/^Exercise \d+$/i.test(h) ? [i] : []));
  if (/^date \(yyyy\/mm\/dd\)$/i.test((header[0] ?? '').replace(/^\uFEFF/, '')) && header.some((h) => /^set 1 \(reps\)$/i.test(h))) return parseStrongLifts2025(rows);
  const isLegacy = /^date$/i.test(header[0] ?? '') && blocks.length > 0 && blocks.every((i) => /^Weight \(KG\)$/i.test(header[i + 1] ?? '') && /^Weight \(LB\)$/i.test(header[i + 2] ?? '') && /^Set 1$/i.test(header[i + 3] ?? ''));
  if (!isLegacy) throw new ImportError('unrecognised', "This doesn't look like a StrongLifts export we can read yet. Send us the file and we'll add support for it.");
  const col = (re: RegExp) => header.findIndex((h) => re.test(h));
  const noteCol = col(/^note$/i);
  const bwKg = col(/^body weight \(kg\)$/i);
  const bwLb = col(/^body weight \(lb\)$/i);
  const setsPer = (i: number) => {
    let n = 0;
    while (/^Set \d+$/i.test(header[i + 3 + n] ?? '')) n++;
    return n;
  };

  // Day/month order: decided for the whole file, from any date that can only be read one way.
  const dates = rows.slice(1).map((r) => /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec((r[0] ?? '').trim()));
  const firstBig = dates.some((m) => m && +m[1]! > 12);
  const secondBig = dates.some((m) => m && +m[2]! > 12);
  if (firstBig && secondBig) throw new ImportError('mixed_dates', 'The dates in this file are in more than one format.');
  const assumed = !firstBig && !secondBig;
  // The verified sample is month/day; the user can say otherwise when the file can't tell us.
  const dateOrder = firstBig ? 'dmy' : secondBig ? 'mdy' : (opts.dateOrder ?? 'mdy');

  const workouts: ImportedWorkout[] = [];
  const skipped: ParsedImport['skipped'] = [];
  const unmapped = new Set<string>();
  rows.slice(1).forEach((r, k) => {
    const line = k + 2;
    const m = dates[k];
    if (!m) return void skipped.push({ line, reason: 'no date' });
    const [mo, d] = dateOrder === 'mdy' ? [+m[1]!, +m[2]!] : [+m[2]!, +m[1]!];
    const y = m[3]!.length === 2 ? 2000 + +m[3]! : +m[3]!;
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return void skipped.push({ line, reason: 'bad date' });
    const performedAt = Date.UTC(y, mo - 1, d, 12); // midday UTC: the calendar day survives any time zone
    const exercises: ImportedExercise[] = [];
    let unit: Unit | null = null;
    for (const i of blocks) {
      const name = (r[i] ?? '').trim();
      if (!name) continue;
      const kgW = num(r[i + 1]);
      const lbW = num(r[i + 2]);
      const u: Unit = kgW !== null ? 'kg' : 'lb';
      unit ??= kgW !== null || lbW !== null ? u : null;
      const raw = Array.from({ length: setsPer(i) }, (_, s) => num(r[i + 3 + s]));
      while (raw.length && raw[raw.length - 1] === null) raw.pop(); // trailing blanks = sets not programmed
      if (!raw.length) continue;
      const exercise = mapExercise(name);
      if (!exercise) unmapped.add(name);
      const w = kgW ?? lbW;
      exercises.push({ exercise, name, weight: exercise === 'pushup' || w === null ? null : mass(w), reps: raw.map((x) => (x === null ? null : Math.max(0, Math.round(x)))) });
    }
    if (!exercises.length) return void skipped.push({ line, reason: 'no exercises' });
    const bw = num(r[bwKg]) ?? num(r[bwLb]);
    const note = noteCol >= 0 ? (r[noteCol] ?? '').trim() : '';
    const iso = new Date(performedAt).toISOString().slice(0, 10);
    const kind = inferKind(exercises);
    const name = kind ? `Workout ${kind}` : 'Workout';
    workouts.push({
      key: `stronglifts:${iso}:${name}`,
      performedAt,
      name,
      kind,
      unit: unit ?? (num(r[bwLb]) !== null && num(r[bwKg]) === null ? 'lb' : 'kg'),
      ...(bw !== null ? { bodyweight: mass(bw) } : {}),
      ...(note ? { note } : {}),
      exercises,
    });
  });
  workouts.sort((a, b) => a.performedAt - b.performedAt);
  return { format: 'stronglifts-legacy', workouts, unmapped: [...unmapped], dateOrder, dateOrderAssumed: assumed, skipped };
}

/**
 * StrongLifts' current export (seen 13 Oct 2026): one row per exercise per workout, dates as yyyy/mm/dd, a workout
 * number, "Workout A/B", body weight, and per-set `Set N (Reps)` / `Set N (KG|LB)` pairs. Rows with only timed sets
 * are skipped. When a row's sets use different weights, the exercise's weight is the one used on most sets (ties:
 * the heavier), since history holds one weight per exercise.
 */
function parseStrongLifts2025(rows: string[][]): ParsedImport {
  const header = rows[0]!.map((h) => h.replace(/^﻿/, '').trim());
  const col = (re: RegExp) => header.findIndex((h) => re.test(h));
  const cNum = col(/^workout$/i);
  const cName = col(/^workout name$/i);
  const cEx = col(/^exercise$/i);
  const cNote = col(/^notes?$/i);
  const cBwKg = col(/^body weight \(kg\)$/i);
  const cBwLb = col(/^body weight \(lb\)$/i);
  const unit: Unit = header.some((h) => /^set 1 \(lb\)$/i.test(h)) ? 'lb' : 'kg';
  const sets: { reps: number; w: number }[] = [];
  for (let n = 1; n < 100; n++) {
    const r = header.findIndex((h) => h.toLowerCase() === `set ${n} (reps)`);
    if (r < 0) break;
    sets.push({ reps: r, w: header.findIndex((h) => h.toLowerCase() === `set ${n} (kg)` || h.toLowerCase() === `set ${n} (lb)`) });
  }
  const skipped: ParsedImport['skipped'] = [];
  const unmapped = new Set<string>();
  const groups = new Map<string, { performedAt: number; iso: string; name: string; num: string; bw: number | null; note: string; exercises: ImportedExercise[] }>();
  rows.slice(1).forEach((r, k) => {
    const line = k + 2;
    if (r.every((c) => !c.trim())) return;
    const m = /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/.exec((r[0] ?? '').replace(/^﻿/, '').trim());
    if (!m) return void skipped.push({ line, reason: 'no date' });
    const [y, mo, d] = [+m[1]!, +m[2]!, +m[3]!];
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return void skipped.push({ line, reason: 'bad date' });
    const name = (r[cEx] ?? '').trim();
    if (!name) return void skipped.push({ line, reason: 'no exercise' });
    const raw = sets.map((c) => ({ reps: num(r[c.reps]), w: c.w >= 0 ? num(r[c.w]) : null }));
    while (raw.length && raw[raw.length - 1]!.reps === null) raw.pop(); // trailing blanks = sets not programmed
    if (!raw.length) return void skipped.push({ line, reason: "no reps (timed sets aren't imported)" });
    const exercise = mapExercise(name);
    if (!exercise) unmapped.add(name);
    const counts = new Map<number, number>();
    for (const x of raw) if (x.reps !== null && x.w !== null) counts.set(x.w, (counts.get(x.w) ?? 0) + 1);
    const w = [...counts].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0]?.[0] ?? null;
    const iso = `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const wName = ((cName >= 0 ? r[cName] : '') ?? '').trim() || 'Workout';
    const num0 = ((cNum >= 0 ? r[cNum] : '') ?? '').trim();
    const gk = `${iso}|${num0}|${wName}`;
    const g = groups.get(gk) ?? { performedAt: Date.UTC(y, mo - 1, d, 12), iso, name: wName, num: num0, bw: num(r[cBwKg]) ?? num(r[cBwLb]), note: '', exercises: [] };
    const note = cNote >= 0 ? (r[cNote] ?? '').trim() : '';
    if (note && !g.note.includes(note)) g.note = g.note ? `${g.note} · ${note}` : note;
    g.exercises.push({ exercise, name, weight: exercise === 'pushup' || w === null ? null : mass(w), reps: raw.map((x) => (x.reps === null ? null : Math.max(0, Math.round(x.reps)))) });
    groups.set(gk, g);
  });
  const workouts: ImportedWorkout[] = [...groups.values()].map((g) => {
    const named = /^workout ([ab])$/i.exec(g.name)?.[1]?.toUpperCase() as WorkoutKind | undefined;
    const kind = named ?? inferKind(g.exercises);
    return {
      key: `stronglifts:${g.iso}:${g.name}${g.num ? `#${g.num}` : ''}`,
      performedAt: g.performedAt,
      name: kind ? `Workout ${kind}` : g.name,
      kind,
      unit,
      ...(g.bw !== null ? { bodyweight: mass(g.bw) } : {}),
      ...(g.note ? { note: g.note } : {}),
      exercises: g.exercises,
    };
  });
  if (!workouts.length) throw new ImportError('empty', 'This file has no workouts in it.');
  workouts.sort((a, b) => a.performedAt - b.performedAt);
  return { format: 'stronglifts-2025', workouts, unmapped: [...unmapped], dateOrder: 'mdy', dateOrderAssumed: false, skipped };
}

/** Imported workouts still present (not deleted), in date order, each with the unit its weights were written in. */
export function importedWorkouts(events: readonly RepwiseEvent[]): (EventOf<'workout_imported'> & { unit: Unit })[] {
  const deleted = new Set(events.filter((e) => e.type === 'workout_deleted').map((e) => (e as EventOf<'workout_deleted'>).workoutId));
  const unitAt = unitTimeline(events);
  const seen = new Set<string>();
  const out: (EventOf<'workout_imported'> & { unit: Unit })[] = [];
  events.forEach((e, i) => {
    if (e.type !== 'workout_imported' || deleted.has(e.workoutId) || seen.has(e.key)) return;
    seen.add(e.key);
    out.push({ ...e, unit: e.unit ?? unitAt(i) });
  });
  return out.sort((a, b) => a.performedAt - b.performedAt);
}

/** Events for the workouts not already imported; weights converted to the programme unit (rounded down, ADR-0017). */
export function importEvents(existing: readonly RepwiseEvent[], parsed: ParsedImport, unit: Unit, newId: () => string, now: number): { events: EventOf<'workout_imported'>[]; duplicates: number } {
  const have = new Set(importedWorkouts(existing).map((e) => e.key));
  const conv = (m: Mass, from: Unit) => convert(m, from, unit, 25);
  const events: EventOf<'workout_imported'>[] = [];
  let duplicates = 0;
  for (const w of parsed.workouts) {
    if (have.has(w.key)) {
      duplicates++;
      continue;
    }
    have.add(w.key);
    events.push({
      type: 'workout_imported',
      id: newId(),
      at: now,
      workoutId: newId(),
      source: SOURCE_OF[parsed.format],
      unit,
      key: w.key,
      performedAt: w.performedAt,
      name: w.name,
      kind: w.kind,
      ...(w.bodyweight !== undefined ? { bodyweight: conv(w.bodyweight, w.unit) } : {}),
      ...(w.note ? { note: w.note } : {}),
      exercises: w.exercises.map((x) => ({ ...x, weight: x.weight === null ? null : conv(x.weight, w.unit) })),
    });
  }
  return { events, duplicates };
}

export interface ManualWorkoutInput {
  performedAt: number;
  kind: WorkoutKind;
  lifts: { lift: MainLift; weight: Mass; reps: (number | null)[] }[];
  note?: string;
}

/** Problems with a hand-entered past workout, in the order the form shows them. */
/**
 * The date is a calendar day (stored at midday UTC on that day), so "not in the future" compares DAYS in the user's
 * time zone: today is always allowed, whatever the time (bug 14 Oct 2026: refused before 12:00 UTC).
 */
export function validateManualWorkout(w: ManualWorkoutInput, now: number, timeZone = 'UTC'): string[] {
  const errors: string[] = [];
  if (!Number.isFinite(w.performedAt) || new Date(w.performedAt).toISOString().slice(0, 10) > localDay(now, timeZone)) errors.push("The date can't be in the future.");
  if (!w.lifts.length) errors.push('Add at least one lift.');
  for (const l of w.lifts) {
    if (!(l.weight > 0) || l.weight > 100_000) errors.push(`Check the weight for ${l.lift}.`);
    if (!l.reps.length || l.reps.some((r) => r !== null && (!Number.isInteger(r) || r < 0 || r > 30))) errors.push(`Check the reps for ${l.lift}.`);
  }
  return errors;
}

/** A past workout typed in by hand (ADR-0017): history-only, exactly like an import. */
export function manualWorkoutEvent(w: ManualWorkoutInput, newId: () => string, now: number, unit?: Unit): EventOf<'workout_imported'> {
  const workoutId = newId();
  return {
    type: 'workout_imported',
    id: newId(),
    at: now,
    workoutId,
    source: 'manual',
    ...(unit ? { unit } : {}),
    key: `manual:${workoutId}`,
    performedAt: w.performedAt,
    name: `Workout ${w.kind}`,
    kind: w.kind,
    ...(w.note?.trim() ? { note: w.note.trim().slice(0, 500) } : {}),
    exercises: w.lifts.map((l) => ({ exercise: l.lift, name: l.lift, weight: l.weight, reps: l.reps })),
  };
}

export interface ContinueSuggestion {
  lastAt: number;
  lifts: { lift: MainLift; last: Mass; outcome: SessionOutcome; next: Mass }[];
  nextKind: WorkoutKind | null;
  /** ADR-0009 applied to the import: days since the last imported workout, and the reduction applied (0 = none). */
  daysSince?: number;
  breakFraction?: number;
}

const MAIN: readonly MainLift[] = ['squat', 'bench', 'row', 'ohp', 'deadlift'];
const TARGET_REPS = Object.fromEntries(MAIN.map((l) => [l, (WORKOUTS.A.find((p) => p.lift === l) ?? WORKOUTS.B.find((p) => p.lift === l))!.reps])) as Record<MainLift, number>;

/**
 * "Continue from your last StrongLifts workout": each lift's next weight, from replaying its imported
 * sessions through the engine's own rule (so rounding, misses and the after-first-miss increment match).
 * Null when there's nothing to continue from, or Repwise already has a newer completed workout.
 */
/**
 * Where to continue from imported history. With `now`, a long gap since the last imported workout eases back in
 * exactly as after a break in Repwise (ADR-0009): 7/14/28+ days → 10/20/30% lighter, floored to loadable.
 */
export function continueFromImport(events: readonly RepwiseEvent[], settings: Settings, lastCompletedAt?: number, now?: number, timeZone = 'UTC'): ContinueSuggestion | null {
  const imported = importedWorkouts(events);
  const last = imported[imported.length - 1];
  if (!last || (lastCompletedAt !== undefined && lastCompletedAt >= last.performedAt)) return null;
  const lifts: ContinueSuggestion['lifts'] = [];
  for (const lift of MAIN) {
    let st: LiftState | null = null;
    let outcome: SessionOutcome = 'skipped';
    let lastW: Mass | null = null;
    for (const w of imported) {
      const x = w.exercises.find((e) => e.exercise === lift && e.weight !== null && e.reps.length > 0);
      if (!x) continue;
      outcome = classify(x.reps, x.reps.length, TARGET_REPS[lift]);
      const weight = w.unit === settings.unit ? x.weight! : convert(x.weight!, w.unit, settings.unit, 25); // ADR-0018
      const before: LiftState = { weight, misses: st?.misses ?? 0, hasMissed: st?.hasMissed ?? false };
      st = decide(before, settings.lifts[lift], outcome, settings.gym).next;
      lastW = weight;
    }
    if (st && lastW !== null) lifts.push({ lift, last: lastW, outcome, next: st.weight });
  }
  const daysSince = now === undefined ? 0 : calendarDaysBetween(last.performedAt, now, timeZone);
  const f = breakReduction(daysSince);
  const eased = f > 0 ? lifts.map((l) => ({ ...l, next: restartWeight(settings.gym, l.next, f) })) : lifts;
  return { lastAt: last.performedAt, lifts: eased, nextKind: last.kind ? (last.kind === 'A' ? 'B' : 'A') : null, ...(now !== undefined ? { daysSince, breakFraction: f } : {}) };
}

// ── Strong and Hevy (ADR-0017; formats from research/05 §3.2–3.3, real sample exports) ─────────────────────────

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** "2024-11-28 17:36:08" (Strong) or "15 Sep 2025, 07:48" (Hevy) → calendar date and the original time text. */
function parseStamp(s: string): { y: number; mo: number; d: number; time: string } | null {
  const t = s.trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}:\d{2})/.exec(t);
  if (m) return { y: +m[1]!, mo: +m[2]!, d: +m[3]!, time: m[4]! };
  m = /^(\d{1,2}) ([A-Za-z]{3})[a-z]* (\d{4}),? (\d{1,2}:\d{2})/.exec(t);
  if (m) {
    const mo = MONTHS.indexOf(m[2]!.toLowerCase()) + 1;
    return mo ? { y: +m[3]!, mo, d: +m[1]!, time: m[4]! } : null;
  }
  return null;
}

type Rec = Record<string, string>;
function records(text: string): { header: string[]; rows: Rec[] } {
  const rows = parseCsv(text);
  const header = (rows[0] ?? []).map((h) => h.trim().toLowerCase());
  return { header, rows: rows.slice(1).map((r) => Object.fromEntries(header.map((h, i) => [h, (r[i] ?? '').trim()]))) };
}

interface RawSet {
  exercise: string;
  order: number;
  weight: number | null;
  reps: number | null;
  warmup: boolean;
}

/** One workout from flat set rows: group sets by (exercise, weight), heaviest first; warm-ups left out. */
function buildWorkout(source: ImportSource, stamp: { y: number; mo: number; d: number; time: string }, name: string, unit: Unit, sets: RawSet[], notes: string[], unmapped: Set<string>): ImportedWorkout | null {
  const byEx = new Map<string, RawSet[]>();
  for (const s of sets) if (!s.warmup && s.reps !== null) byEx.set(s.exercise, [...(byEx.get(s.exercise) ?? []), s]);
  const exercises: ImportedExercise[] = [];
  for (const [exName, list] of byEx) {
    const exercise = mapExercise(exName);
    if (!exercise) unmapped.add(exName);
    const groups = new Map<number | null, RawSet[]>();
    for (const s of [...list].sort((a, b) => a.order - b.order)) groups.set(s.weight, [...(groups.get(s.weight) ?? []), s]);
    for (const [w, g] of [...groups.entries()].sort((a, b) => (b[0] ?? 0) - (a[0] ?? 0)))
      exercises.push({ exercise, name: exName, weight: exercise === 'pushup' || w === null || w === 0 ? null : mass(w), reps: g.map((s) => Math.max(0, Math.round(s.reps!))) });
  }
  if (!exercises.length) return null;
  const performedAt = Date.UTC(stamp.y, stamp.mo - 1, stamp.d, 12);
  const iso = new Date(performedAt).toISOString().slice(0, 10);
  const note = notes.filter(Boolean).join(' · ');
  return { key: `${source}:${iso}:${stamp.time}:${name}`, performedAt, name: name || 'Workout', kind: inferKind(exercises), unit, ...(note ? { note: note.slice(0, 1000) } : {}), exercises };
}

/** Strong: legacy comma layout (unit not in the header, so the caller says) or the newer semicolon layout. */
export function parseStrong(text: string, opts: { unit?: Unit } = {}): ParsedImport & { unitAssumed: boolean } {
  const { header, rows } = records(text);
  if (!header.includes('exercise name') || !header.includes('set order') || !header.some((h) => h === 'date')) throw new ImportError('unrecognised', "This doesn't look like a Strong export.");
  const wCol = header.find((h) => /^weight( \((kg|lb|lbs)\))?$/.test(h))!;
  const headerUnit: Unit | null = /\(kg\)/.test(wCol ?? '') ? 'kg' : /\(lbs?\)/.test(wCol ?? '') ? 'lb' : null;
  const unit = headerUnit ?? opts.unit ?? 'kg';
  const groups = new Map<string, { stamp: NonNullable<ReturnType<typeof parseStamp>>; name: string; sets: RawSet[]; notes: Set<string> }>();
  const skipped: ParsedImport['skipped'] = [];
  rows.forEach((r, i) => {
    if (/rest timer/i.test(r['set order'] ?? '')) return;
    const stamp = parseStamp(r['date'] ?? '');
    if (!stamp) return void skipped.push({ line: i + 2, reason: 'no date' });
    const name = (r['workout name'] ?? '').trim();
    const k = `${r['date']}|${name}`;
    const g = groups.get(k) ?? { stamp, name, sets: [], notes: new Set<string>() };
    g.sets.push({ exercise: r['exercise name'] ?? '', order: Number(r['set order']) || 0, weight: num(r[wCol]), reps: num(r['reps']), warmup: /^w/i.test(r['set order'] ?? '') });
    if (r['workout notes']) g.notes.add(r['workout notes']);
    if (r['notes']) g.notes.add(`${r['exercise name']}: ${r['notes']}`);
    groups.set(k, g);
  });
  const unmapped = new Set<string>();
  const workouts = [...groups.values()].flatMap((g) => buildWorkout('strong', g.stamp, g.name, unit, g.sets, [...g.notes], unmapped) ?? []).sort((a, b) => a.performedAt - b.performedAt);
  if (!workouts.length) throw new ImportError('empty', 'This file has no workouts in it.');
  return { format: 'strong', workouts, unmapped: [...unmapped], dateOrder: 'mdy', dateOrderAssumed: false, skipped, unitAssumed: headerUnit === null };
}

/** Hevy: one row per set; a workout is the rows sharing title + start_time; weight_kg or weight_lbs. */
export function parseHevy(text: string): ParsedImport & { unitAssumed: boolean } {
  const { header, rows } = records(text);
  if (!header.includes('exercise_title') || !header.includes('start_time') || !header.includes('set_index')) throw new ImportError('unrecognised', "This doesn't look like a Hevy export.");
  const wCol = header.includes('weight_kg') ? 'weight_kg' : header.includes('weight_lbs') ? 'weight_lbs' : null;
  const unit: Unit = wCol === 'weight_lbs' ? 'lb' : 'kg';
  const groups = new Map<string, { stamp: NonNullable<ReturnType<typeof parseStamp>>; name: string; sets: RawSet[]; notes: Set<string> }>();
  const skipped: ParsedImport['skipped'] = [];
  rows.forEach((r, i) => {
    const stamp = parseStamp(r['start_time'] ?? '');
    if (!stamp) return void skipped.push({ line: i + 2, reason: 'no date' });
    const name = r['title'] ?? '';
    const k = `${r['start_time']}|${name}`;
    const g = groups.get(k) ?? { stamp, name, sets: [], notes: new Set<string>() };
    g.sets.push({ exercise: r['exercise_title'] ?? '', order: Number(r['set_index']) || 0, weight: wCol ? num(r[wCol]) : null, reps: num(r['reps']), warmup: (r['set_type'] ?? '') === 'warmup' });
    if (r['description']) g.notes.add(r['description']);
    if (r['exercise_notes']) g.notes.add(`${r['exercise_title']}: ${r['exercise_notes']}`);
    groups.set(k, g);
  });
  const unmapped = new Set<string>();
  const workouts = [...groups.values()].flatMap((g) => buildWorkout('hevy', g.stamp, g.name, unit, g.sets, [...g.notes], unmapped) ?? []).sort((a, b) => a.performedAt - b.performedAt);
  if (!workouts.length) throw new ImportError('empty', 'This file has no workouts in it.');
  return { format: 'hevy', workouts, unmapped: [...unmapped], dateOrder: 'mdy', dateOrderAssumed: false, skipped, unitAssumed: wCol === null };
}

/** Pick the parser from the header row (research/05 §3: detect, never guess). */
export function parseHistory(text: string, opts: { unit?: Unit; dateOrder?: 'mdy' | 'dmy' } = {}): ParsedImport & { unitAssumed?: boolean } {
  const head = parseCsv(text.split(/\r?\n/, 1)[0] ?? '')[0]?.map((h) => h.trim().toLowerCase()) ?? [];
  if (head.includes('exercise_title')) return parseHevy(text);
  if (head.includes('exercise name') && head.includes('set order')) return parseStrong(text, opts);
  return parseStrongLifts(text, opts);
}

export const SOURCE_OF: Record<ImportFormat, ImportSource> = { 'stronglifts-legacy': 'stronglifts', 'stronglifts-2025': 'stronglifts', strong: 'strong', hevy: 'hevy' };
